import React, { Key, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import { EntityTypeSupportingGroups, Group } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { EntityTypeShapedIcon, Popconfirm, SearchField } from "@mapmycustomers/ui";

import { CreateGroupModal } from "@app/component/createEditEntity/Group";
import AddOrCreateButton from "@app/component/preview/components/AddOrCreateButton";
import Table from "@app/component/Table";
import useMapView from "@app/component/view/utils/useMapView";
import LassoToolMode from "@app/scene/map/enums/LassoToolMode";
import MapMode from "@app/scene/map/enums/MapMode";
import { selectMapTool } from "@app/scene/map/store/actions";
import { getMapMode } from "@app/scene/map/store/selectors";
import { getGroupsForEntity, isAnythingLoading } from "@app/store/groups";
import { getCurrentUser, isCurrentUserReadOnly } from "@app/store/iam";
import { isOwner } from "@app/store/iam/util";
import { RootState } from "@app/store/rootReducer";
import { TOPMOST_MODAL_ZINDEX } from "@app/util/consts";
import useAnalytics from "@app/util/contexts/useAnalytics";

import useColumns from "../useColumns";

import BulkActions from "./BulkActions";
import styles from "./GroupsTab.module.scss";
import messages from "./messages";
import NoData from "./NoData";

interface Props {
  currentUser: User;
  entityType: EntityTypeSupportingGroups;
  groups: Group[];
  isReadOnlyMember: boolean;
  loading: boolean;
  mode?: MapMode;
  onClick?: (group: Group, entityType: EntityTypeSupportingGroups) => void;
  onHide?: () => void;
  selectMapTool?: typeof selectMapTool;
}

const GroupsTab: React.FC<Props> = ({
  currentUser,
  entityType,
  groups,
  isReadOnlyMember,
  loading,
  mode,
  onClick,
  onHide,
  selectMapTool,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const [filter, setFilter] = useState("");
  const [selectedGroupIds, setSelectedGroupIds] = useState<Group["id"][]>([]);

  const { updateViewStateAndFetch } = useMapView();
  const columns = useColumns(entityType);

  const filteredGroups = useMemo(() => {
    const q = filter.toLowerCase();
    return groups.filter(({ name }) => name.toLowerCase().includes(q)).sort(nameComparator);
  }, [filter, groups]);

  const [createGroupModelVisible, showCreateGroupModal, hideCreateGroupModal] = useBoolean();

  const handleShowCreateGroupModal = useCallback(() => {
    showCreateGroupModal();
    analytics.clicked(["Create new"]);
  }, [analytics, showCreateGroupModal]);

  const handleGroupCreation = useCallback(
    (group: Group, shouldAddPins?: boolean) => {
      if (shouldAddPins) {
        selectMapTool?.({
          mapTool: MapTool.LASSO,
          mapToolOptions: {
            addItemsToGroupId: group.id,
            mode: LassoToolMode.ADD_GROUP_PINS,
            visibleEntityTypes: [entityType],
          },
        });
        updateViewStateAndFetch({ request: {} });
      }
    },
    [entityType, selectMapTool, updateViewStateAndFetch]
  );

  const handleSearch = useCallback(
    (query: string) => {
      setFilter(query.toLowerCase());
      analytics.using(["Search"]);
    },
    [analytics]
  );

  const showCreateConfirmation = [MapMode.GROUPS, MapMode.TERRITORIES].includes(mode!);

  const handleSelectionChange = useCallback(
    (keys: Key[]) => setSelectedGroupIds?.(keys as Group["id"][]),
    []
  );

  const rowEventHandlers = useCallback(
    (group: Group) => ({
      onClick: () => {
        onClick?.(group, entityType);
      },
    }),
    [entityType, onClick]
  );

  const handleClearSearch = useCallback(() => setFilter(""), []);

  const handleHideCreateGroupModal = useCallback(
    (_, addItems?: boolean) => {
      hideCreateGroupModal();
      if (addItems) {
        onHide?.();
      }
    },
    [hideCreateGroupModal, onHide]
  );

  useEffect(() => {
    setSelectedGroupIds([]);
    setFilter("");
  }, [entityType, groups]);

  return (
    <div className={styles.container}>
      <>
        <div className={styles.header}>
          <div className={styles.title}>
            <EntityTypeShapedIcon className={styles.entityIcon} entityType={entityType} />
            {intl.formatMessage(messages.title, {
              count: filteredGroups.length,
              entityType,
              total: groups.length,
            })}
          </div>
          <div className={styles.actions}>
            {showCreateConfirmation && !isReadOnlyMember ? (
              <Popconfirm
                cancelButtonProps={{ className: styles.confirmationCancel }}
                cancelText={intl.formatMessage(messages.addConfirmationCancel)}
                description={intl.formatMessage(
                  mode === MapMode.TERRITORIES
                    ? messages.addConfirmationTerritory
                    : messages.addConfirmationGroup
                )}
                okText={intl.formatMessage(messages.addConfirmationOk)}
                onConfirm={handleShowCreateGroupModal}
                overlayClassName={styles.confirmation}
                placement="right"
                zIndex={TOPMOST_MODAL_ZINDEX}
              >
                <AddOrCreateButton label={intl.formatMessage(messages.addGroup)} />
              </Popconfirm>
            ) : (
              <AddOrCreateButton
                label={intl.formatMessage(messages.addGroup)}
                onClick={handleShowCreateGroupModal}
              />
            )}
          </div>
        </div>
        <div className={styles.groups}>
          <div className={styles.search}>
            <SearchField
              allowClear
              className={styles.searchField}
              onChange={handleSearch}
              placeholder={intl.formatMessage(messages.placeholder)}
              size="middle"
              value={filter}
            />
            {selectedGroupIds.length > 0 && (
              <BulkActions entityType={entityType} selectedGroupIds={selectedGroupIds} />
            )}
          </div>
          {filteredGroups.length > 0 ? (
            <Table<Group>
              className={styles.table}
              columns={columns}
              dataSource={filteredGroups}
              loading={loading}
              onRow={rowEventHandlers}
              pagination={false}
              rowKey="id"
              rowSelection={{
                getCheckboxProps: (record: Group) => ({
                  disabled: record.user.id !== currentUser.id && !isOwner(currentUser), // Column configuration not to be checked
                }),
                onChange: handleSelectionChange,
                selectedRowKeys: selectedGroupIds,
              }}
              scrollable
              showBorder
            />
          ) : (
            <NoData
              addGroupDisabled={isReadOnlyMember}
              entityType={entityType}
              noGroups={groups.length === 0}
              onClearSearch={handleClearSearch}
              onCreateGroup={handleShowCreateGroupModal}
            />
          )}
        </div>
      </>
      <CreateGroupModal
        entityType={entityType}
        onCreate={handleGroupCreation}
        onHide={handleHideCreateGroupModal}
        showCreateOptions
        visible={createGroupModelVisible}
      />
    </div>
  );
};

const mapStateToProps = (
  state: RootState,
  { entityType }: { entityType: EntityTypeSupportingGroups }
) => ({
  currentUser: getCurrentUser(state)!,
  groups: getGroupsForEntity(state)(entityType),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  loading: isAnythingLoading(state),
  mode: getMapMode(state),
});

const mapDispatchToProps = {
  selectMapTool,
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupsTab);
